import { type ReactNode, useEffect, useRef } from 'react'
import { Link, usePage, router } from '@inertiajs/react'
import { Menu } from 'lucide-react'
import { toast } from 'sonner'
import { Toaster } from '@/components/ui/sonner'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from '@/components/ui/sheet'
import type { SharedProps } from '@/types'
import Logo from '@/components/Logo'
import NewsletterSignup from '@/components/NewsletterSignup'
import NewsletterModal from '@/components/NewsletterModal'
import NotificationBell from '@/components/NotificationBell'

export default function Layout({ children, hideNewsletterSignup = false }: { children: ReactNode; hideNewsletterSignup?: boolean }) {
  const { current_user: currentUser, current_organization: currentOrganization, flash, csrf_token: csrfToken } = usePage<SharedProps>().props
  const flashRef = useRef<string | null>(null)

  // Keep CSRF meta tag in sync after Inertia navigations (especially post-login reset_session)
  useEffect(() => {
    const meta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
    if (meta && csrfToken) {
      meta.setAttribute('content', csrfToken)
    }
  }, [csrfToken])

  useEffect(() => {
    const flashKey = JSON.stringify(flash)
    if (flashKey === flashRef.current) return
    flashRef.current = flashKey

    if (flash.success) toast.success(flash.success)
    if (flash.alert) toast.error(flash.alert)
    if (flash.notice) toast.info(flash.notice)
  }, [flash])

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-right" richColors />
      <NewsletterModal />
      <nav className="border-b border-border/50 bg-background/80 backdrop-blur-md sticky top-0 z-50">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          {/* Left: logo + primary nav links */}
          <div className="flex items-center gap-6">
            <Logo className="text-2xl" />
            <div className="hidden md:flex items-center gap-4">
              {currentUser && (
                <Link
                  href="/feed"
                  className="text-base text-muted-foreground hover:text-foreground"
                >
                  Feed
                </Link>
              )}
              {currentUser && (
                <Link
                  href="/network"
                  className="text-base text-muted-foreground hover:text-foreground"
                >
                  Network
                </Link>
              )}
              <Link
                href="/events"
                className="text-base text-muted-foreground hover:text-foreground"
              >
                Discover
              </Link>
              <Link
                href="/for-nonprofits"
                className="text-base text-muted-foreground hover:text-foreground"
              >
                For Nonprofits
              </Link>
              <Link
                href="/services"
                className="text-base text-muted-foreground hover:text-foreground"
              >
                Event Services
              </Link>
              <Link
                href="/philanthropists"
                className="text-base text-muted-foreground hover:text-foreground"
              >
                Philanthropists
              </Link>
            </div>
          </div>

          {/* Desktop nav: auth-state actions on the right */}
          <div className="hidden md:flex items-center gap-4">
            {!currentUser && !currentOrganization && (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline">Sign In</Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem asChild>
                      <Link href="/users/sign_in">As a Philanthropist</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href="/organizations/sign_in">As an Organization</Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button>Sign Up</Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem asChild>
                      <Link href="/users/sign_up">As a Philanthropist</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href="/organizations/sign_up">As an Organization</Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}

            {currentUser && (
              <>
                <NotificationBell />
                <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback>
                        {currentUser.first_name[0]}{currentUser.last_name[0]}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                  <DropdownMenuLabel>
                    {currentUser.first_name} {currentUser.last_name}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link href={`/u/${currentUser.username}`}>Profile</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/network">Network</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/my-tickets">My tickets</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/settings">Settings</Link>
                  </DropdownMenuItem>
                  {currentUser.admin && (
                    <DropdownMenuItem asChild>
                      <Link href="/admin">Admin</Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => router.delete('/users/sign_out')}>
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              </>
            )}

            {currentOrganization && (
              <>
                <Link
                  href="/dashboard"
                  className="text-base text-muted-foreground hover:text-foreground"
                >
                  Dashboard
                </Link>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback>
                          {currentOrganization.name[0]}
                        </AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44">
                    <DropdownMenuLabel className="flex items-center gap-2">
                      {currentOrganization.name}
                      {currentOrganization.pro && (
                        <Badge variant="default" className="text-[10px] px-1.5 py-0">Pro</Badge>
                      )}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link href="/dashboard">Dashboard</Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={() => router.delete('/organizations/sign_out')}>
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>

          {/* Mobile hamburger */}
            <div className="md:hidden">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Open menu">
                    <Menu className="size-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-72">
                  <nav className="flex flex-col gap-1 pt-6">
                    {!currentUser && !currentOrganization && (
                      <>
                        <SheetClose asChild>
                          <Link href="/events" className="rounded-md px-3 py-2 text-base font-medium hover:bg-accent">
                            Discover
                          </Link>
                        </SheetClose>
                        <SheetClose asChild>
                          <Link href="/for-nonprofits" className="rounded-md px-3 py-2 text-base hover:bg-accent">
                            For Nonprofits
                          </Link>
                        </SheetClose>
                        <SheetClose asChild>
                          <Link href="/services" className="rounded-md px-3 py-2 text-base hover:bg-accent">
                            Event Services
                          </Link>
                        </SheetClose>
                        <SheetClose asChild>
                          <Link href="/philanthropists" className="rounded-md px-3 py-2 text-base hover:bg-accent">
                            Philanthropists
                          </Link>
                        </SheetClose>
                        <div className="my-2 border-t border-border/50" />
                        <p className="px-3 py-1 text-sm font-medium text-muted-foreground">Sign In</p>
                        <SheetClose asChild>
                          <Link href="/users/sign_in" className="rounded-md px-3 py-2 text-base hover:bg-accent">
                            As a Philanthropist
                          </Link>
                        </SheetClose>
                        <SheetClose asChild>
                          <Link href="/organizations/sign_in" className="rounded-md px-3 py-2 text-base hover:bg-accent">
                            As an Organization
                          </Link>
                        </SheetClose>
                        <div className="my-2 border-t border-border/50" />
                        <p className="px-3 py-1 text-sm font-medium text-muted-foreground">Sign Up</p>
                        <SheetClose asChild>
                          <Link href="/users/sign_up" className="rounded-md px-3 py-2 text-base hover:bg-accent">
                            As a Philanthropist
                          </Link>
                        </SheetClose>
                        <SheetClose asChild>
                          <Link href="/organizations/sign_up" className="rounded-md px-3 py-2 text-base hover:bg-accent">
                            As an Organization
                          </Link>
                        </SheetClose>
                      </>
                    )}

                    {currentUser && (
                      <>
                        <SheetClose asChild>
                          <Link href="/feed" className="rounded-md px-3 py-2 text-base font-medium hover:bg-accent">
                            Feed
                          </Link>
                        </SheetClose>
                        <SheetClose asChild>
                          <Link href="/network" className="rounded-md px-3 py-2 text-base hover:bg-accent">
                            Network
                          </Link>
                        </SheetClose>
                        <SheetClose asChild>
                          <Link href="/events" className="rounded-md px-3 py-2 text-base hover:bg-accent">
                            Discover
                          </Link>
                        </SheetClose>
                        <SheetClose asChild>
                          <Link href="/for-nonprofits" className="rounded-md px-3 py-2 text-base hover:bg-accent">
                            For Nonprofits
                          </Link>
                        </SheetClose>
                        <SheetClose asChild>
                          <Link href="/services" className="rounded-md px-3 py-2 text-base hover:bg-accent">
                            Event Services
                          </Link>
                        </SheetClose>
                        <SheetClose asChild>
                          <Link href="/philanthropists" className="rounded-md px-3 py-2 text-base hover:bg-accent">
                            Philanthropists
                          </Link>
                        </SheetClose>
                        <SheetClose asChild>
                          <Link href={`/u/${currentUser.username}`} className="rounded-md px-3 py-2 text-base hover:bg-accent">
                            Profile
                          </Link>
                        </SheetClose>
                        <SheetClose asChild>
                          <Link href="/my-tickets" className="rounded-md px-3 py-2 text-base hover:bg-accent">
                            My tickets
                          </Link>
                        </SheetClose>
                        <SheetClose asChild>
                          <Link href="/notifications" className="rounded-md px-3 py-2 text-base hover:bg-accent">
                            Notifications
                          </Link>
                        </SheetClose>
                        <SheetClose asChild>
                          <Link href="/settings" className="rounded-md px-3 py-2 text-base hover:bg-accent">
                            Settings
                          </Link>
                        </SheetClose>
                        {currentUser.admin && (
                          <SheetClose asChild>
                            <Link href="/admin" className="rounded-md px-3 py-2 text-base hover:bg-accent">
                              Admin
                            </Link>
                          </SheetClose>
                        )}
                        <div className="my-2 border-t border-border/50" />
                        <SheetClose asChild>
                          <button
                            onClick={() => router.delete('/users/sign_out')}
                            className="rounded-md px-3 py-2 text-left text-base text-muted-foreground hover:bg-accent hover:text-foreground"
                          >
                            Sign Out
                          </button>
                        </SheetClose>
                      </>
                    )}

                    {currentOrganization && (
                      <>
                        <SheetClose asChild>
                          <Link href="/dashboard/events" className="rounded-md px-3 py-2 text-base font-medium hover:bg-accent">
                            Dashboard
                          </Link>
                        </SheetClose>
                        <SheetClose asChild>
                          <Link href="/events" className="rounded-md px-3 py-2 text-base hover:bg-accent">
                            Discover
                          </Link>
                        </SheetClose>
                        <SheetClose asChild>
                          <Link href="/for-nonprofits" className="rounded-md px-3 py-2 text-base hover:bg-accent">
                            For Nonprofits
                          </Link>
                        </SheetClose>
                        <SheetClose asChild>
                          <Link href="/services" className="rounded-md px-3 py-2 text-base hover:bg-accent">
                            Event Services
                          </Link>
                        </SheetClose>
                        <SheetClose asChild>
                          <Link href="/philanthropists" className="rounded-md px-3 py-2 text-base hover:bg-accent">
                            Philanthropists
                          </Link>
                        </SheetClose>
                        <div className="my-2 border-t border-border/50" />
                        <SheetClose asChild>
                          <button
                            onClick={() => router.delete('/organizations/sign_out')}
                            className="rounded-md px-3 py-2 text-left text-base text-muted-foreground hover:bg-accent hover:text-foreground"
                          >
                            Sign Out
                          </button>
                        </SheetClose>
                      </>
                    )}
                  </nav>
                </SheetContent>
              </Sheet>
            </div>
        </div>
      </nav>

      <main>{children}</main>

      <footer className="border-t border-border/50 bg-gradient-to-b from-muted/30 to-muted/80">
        <div className="relative mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          {!currentUser && !hideNewsletterSignup && <NewsletterSignup />}
          <div className="grid grid-cols-1 gap-8 md:grid-cols-4">
            <div>
              <Logo className="text-lg" />
              <p className="mt-2 text-base text-muted-foreground text-pretty">
                Where events meet their cause. The platform for nonprofits, philanthropists and event services.
              </p>
            </div>

            <div>
              <h3 className="text-base font-semibold">Links</h3>
              <ul className="mt-2 space-y-2 text-base">
                <li>
                  <Link href="/about" className="text-muted-foreground hover:text-foreground">About</Link>
                </li>
                <li>
                  <Link href="/blog" className="text-muted-foreground hover:text-foreground">Run of Show</Link>
                </li>
                <li>
                  <Link href="/past-events" className="text-muted-foreground hover:text-foreground">Past Events</Link>
                </li>
                <li>
                  <Link href="/contact" className="text-muted-foreground hover:text-foreground">Contact</Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-base font-semibold">Resources</h3>
              <ul className="mt-2 space-y-2 text-base">
                <li>
                  <Link href="/event-announcement-planner" className="text-muted-foreground hover:text-foreground">Event Announcement Planner</Link>
                </li>
                <li>
                  <Link href="/tax-deduction-calculator" className="text-muted-foreground hover:text-foreground">Tax Deduction Calculator</Link>
                </li>
                <li>
                  <Link href="/gala-ticket-pricing-planner" className="text-muted-foreground hover:text-foreground">Gala Ticket Pricing Planner</Link>
                </li>
                <li>
                  <Link href="/seating-planner" className="text-muted-foreground hover:text-foreground">Seating Planner</Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-base font-semibold">Follow Us</h3>
              <div className="mt-2 flex gap-4">
                <a href="https://x.com/galaseason" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground" aria-label="Twitter">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </a>
                <a href="https://www.instagram.com/gala.season/" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground" aria-label="Instagram">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                  </svg>
                </a>
                <a href="https://www.facebook.com/galaseason" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground" aria-label="Facebook">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>
                <a href="https://www.linkedin.com/company/galaseason/" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground" aria-label="LinkedIn">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
                <a href="https://www.threads.com/@gala.season" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground" aria-label="Threads">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.36-.218-3.259-.801-1.063-.69-1.685-1.74-1.752-2.964-.065-1.19.408-2.285 1.33-3.082.88-.76 2.119-1.207 3.583-1.291a13.853 13.853 0 0 1 3.02.142c-.126-.742-.375-1.332-.75-1.757-.513-.586-1.308-.883-2.359-.89h-.029c-.844 0-1.992.232-2.721 1.32L7.734 7.847c.98-1.454 2.568-2.256 4.478-2.256h.044c3.194.02 5.097 1.975 5.287 5.388.108.046.216.094.323.142 1.504.708 2.606 1.78 3.187 3.1.811 1.843.866 4.849-1.578 7.236-1.667 1.628-4.04 2.366-7.255 2.366Zm-1.62-10.083c-.471.04-1.836.247-1.821 1.218.01.642.65 1.273 1.842 1.273.085 0 .172-.003.26-.008.69-.04 1.207-.249 1.536-.62.371-.421.539-1.012.519-1.748a8.097 8.097 0 0 0-1.708-.107Z" />
                  </svg>
                </a>
                <a href="https://www.tiktok.com/@galaseason" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground" aria-label="TikTok">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
                  </svg>
                </a>
                <a href="https://bsky.app/profile/galaseason.bsky.social" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground" aria-label="Bluesky">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M5.202 2.857C7.954 4.922 10.913 9.11 12 11.358c1.087-2.247 4.046-6.436 6.798-8.501C20.783 1.366 24 .213 24 3.883c0 .732-.42 6.156-.667 7.037-.856 3.061-3.978 3.842-6.755 3.37 4.854.826 6.089 3.562 3.422 6.299-5.065 5.196-7.28-1.304-7.847-2.97-.104-.305-.152-.448-.153-.327 0-.121-.05.022-.153.327-.568 1.666-2.782 8.166-7.847 2.97-2.667-2.737-1.432-5.473 3.422-6.3-2.777.473-5.899-.308-6.755-3.369C.42 10.04 0 4.615 0 3.883c0-3.67 3.217-2.517 5.202-1.026" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-col gap-4 border-t border-border/50 pt-8 text-base text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
            <p>&copy; {new Date().getFullYear()} Gala Season. All rights reserved.</p>
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-x-6 sm:gap-y-2">
              <Link href="/privacy" className="hover:text-foreground">Privacy Policy</Link>
              <Link href="/terms" className="hover:text-foreground">Terms of Service</Link>
              <Link href="/community-guidelines" className="hover:text-foreground">Community Guidelines</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
